import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Quote } from 'lucide-react';
import { Reveal, SectionEyebrow, ButtonPrimary, ButtonGhost } from '../components/ui-bits';
import { getClients, getBlog, getNews, getTestimonials } from '../lib/api';
import Seo from '../components/Seo';

const HERO_IMG = 'https://static.wixstatic.com/media/815797_c5cea849b6564df1abeba5e17b9347a3~mv2.jpeg/v1/fill/w_2400,h_1350,fp_0.50_0.28,q_90,enc_avif,quality_auto/815797_c5cea849b6564df1abeba5e17b9347a3~mv2.jpeg';

const pickRandom = (list, n) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, n);
};

export default function Home() {
  const [clients, setClients] = useState([]);
  const [blog, setBlog] = useState([]);
  const [news, setNews] = useState([]);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
    getBlog().then(setBlog).catch(() => {});
    getNews().then(setNews).catch(() => {});
    getTestimonials().then(setQuotes).catch(() => {});
  }, []);

  const featured = useMemo(() => pickRandom(clients.filter(c => c.headshot), 4), [clients]);
  const featuredArticle = blog.find(b => b.featured) || blog[0];
  const latestNews = news.slice(0, 3);

  return (
    <div data-testid="home-page">
      <Seo
        title=""
        description="David Ball Management is a London actors' agency representing actors across television, film, theatre, commercials and voice. Considered, strategic representation with a small, deliberately curated client list."
        path="/"
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative h-[100vh] min-h-[720px] w-full overflow-hidden bg-[#001a38]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover ken-burns opacity-75"
            style={{ objectPosition: 'center 25%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#001a38]/45 via-[#001a38]/35 to-[#001a38]/90" />
        </div>

        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          <Reveal>
            <p className="eyebrow text-[#C5A059]">Est. London · Actors' Representation</p>
          </Reveal>
          <Reveal delay={120}>
            <h1 data-testid="hero-heading" style={{ lineHeight: 1.4, letterSpacing: 0 }} className="mt-8 max-w-xl text-white/90 font-body font-light normal-case text-lg md:text-xl">
              David Ball Management represents actors across television, film, theatre, commercials
              and voice work, building long term careers through considered, strategic representation.
            </h1>
          </Reveal>
          <Reveal delay={340}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-cta-primary">
                <span className="inline-flex items-center gap-3 rounded-full bg-[#C5A059] text-[#001a38] px-8 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-white transition-all duration-500 hover:-translate-y-0.5 shadow-2xl">
                  Apply for Representation <ArrowRight size={14} />
                </span>
              </Link>
              <Link to="/clients" data-testid="hero-cta-secondary">
                <span className="inline-flex items-center gap-3 rounded-full border border-white/40 text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-white hover:text-[#002b5c] transition-all duration-500">
                  Meet Our Clients <ArrowUpRight size={14} />
                </span>
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-8 right-6 lg:right-10 z-10 flex items-center gap-3 text-white/70 text-[10px] tracking-[0.3em] uppercase">
          <span className="w-8 h-px bg-white/40" /> Scroll
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <Reveal className="lg:col-span-4">
            <SectionEyebrow>The Agency</SectionEyebrow>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
              A quiet<br />conviction in<br /><span className="italic font-light">every</span> career.
            </h2>
          </Reveal>
          <Reveal delay={180} className="lg:col-span-7 lg:col-start-6">
            <p className="text-lg md:text-xl text-[#333] leading-relaxed font-light">
              Founded on the belief that actors deserve considered, generous
              representation, David Ball Management is a London agency working closely with a
              deliberately small client list.
            </p>
            <p className="mt-6 text-base text-[#4a4a4a] leading-relaxed">
              We do not chase volume. We build careers with intention, through honest
              conversation, strategic thinking and deep industry relationships cultivated
              across two decades in casting, production and management.
            </p>
            <Link to="/about" className="mt-10 inline-flex items-center gap-3 text-[#002b5c] text-[11px] tracking-[0.24em] uppercase gold-underline">
              Read our story <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY DBM */}
      <section className="bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <Reveal className="lg:col-span-5">
              <SectionEyebrow>Why David Ball Management</SectionEyebrow>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
                Representation,<br />reimagined.
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: '01', t: 'Quality over quantity', d: 'A small, selective client list means real time, real strategy and real relationships.' },
              { n: '02', t: 'The long view', d: 'We think about your long-term aims and how to get there, with a clear strategy that shapes each decision along the way.' },
              { n: '03', t: 'Industry connected', d: 'Two decades of relationships with casting, producers, directors and creative leadership.' },
              { n: '04', t: 'Honest, always', d: 'The right conversation at the right moment, even when it is the difficult one.' },
            ].map((v, i) => (
              <Reveal key={v.n} delay={i * 100}>
                <div className="card-lift bg-white rounded-2xl p-8 h-full border border-black/5">
                  <div className="flex items-baseline justify-between">
                    <span className="font-heading text-[#C5A059] text-2xl">{v.n}</span>
                    <span className="w-6 h-px bg-[#C5A059]" />
                  </div>
                  <h3 className="font-heading uppercase text-[#002b5c] text-2xl md:text-3xl mt-8 leading-tight">{v.t}</h3>
                  <p className="mt-4 text-sm text-[#4a4a4a] leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED COLLABORATORS — colour marquee */}
      <section className="border-y border-black/10 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-10">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <Reveal className="lg:col-span-6">
              <SectionEyebrow>Trusted Collaborators</SectionEyebrow>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
                Industry experience,<br />trusted relationships.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:col-start-8" delay={150}>
              <p className="text-base text-[#4a4a4a] leading-relaxed">
                Our clients work with the most respected companies across British and international
                theatre, television and film.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="relative overflow-hidden py-8 border-t border-black/10">
          {/* Soft fade masks left/right */}
          <div className="pointer-events-none absolute top-0 left-0 bottom-0 w-24 z-10 bg-gradient-to-r from-[#faf8f4] to-transparent" />
          <div className="pointer-events-none absolute top-0 right-0 bottom-0 w-24 z-10 bg-gradient-to-l from-[#faf8f4] to-transparent" />
          <div className="marquee">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-16 lg:gap-24 pr-16 shrink-0">
                {[
                  ['RSC', 'https://static.wixstatic.com/media/815797_0838073bf37a4d00bb560cb23e628192~mv2.jpg/v1/fit/w_260,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['BBC', 'https://static.wixstatic.com/media/815797_bb8c3ec01c9540f9a2e7cc9663fefbf2~mv2.png/v1/fit/w_260,h_180,q_90,enc_avif,quality_auto/logo.png'],
                  ['ITV', 'https://static.wixstatic.com/media/815797_b3714ca146c04b6abdfbddf0d3f1d57d~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Channel 4', 'https://static.wixstatic.com/media/815797_3140a86c4bfc4eaa956e4fa32b9fe4f1~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Netflix', 'https://static.wixstatic.com/media/815797_d7c60d333662486d8a745574b1577f4e~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['BAFTA', 'https://static.wixstatic.com/media/815797_17127c6dc0e6412d85bb74fc2b963eaa~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['National Theatre', 'https://static.wixstatic.com/media/815797_a0fec0119fe34373837101eb06599b30~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Old Vic', 'https://static.wixstatic.com/media/815797_5a2f220d3d9c4f4c875a359c732e7cd1~mv2.gif/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.gif'],
                  ['Young Vic', 'https://static.wixstatic.com/media/815797_802e7efa9ec74d1086c0c1004722d597~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Cameron Mackintosh', 'https://static.wixstatic.com/media/815797_6d9f83d3962d4672b4c34deb7cd85414~mv2.jpeg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpeg'],
                  ['Southwark Playhouse', 'https://static.wixstatic.com/media/815797_829ce099dad14c51aa385329ab325722~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Curve Theatre', 'https://static.wixstatic.com/media/815797_a33347b1cf2b4447bfcc2c8f3f659d22~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Salisbury Playhouse', 'https://static.wixstatic.com/media/815797_015955ce005342ea9e0c66ff0f8abf70~mv2.jpg/v1/fit/w_260,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Gordon Craig', 'https://static.wixstatic.com/media/815797_ece280dc9fbd42aa8d1294ca8afdb55a~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Guildford Shakespeare', 'https://static.wixstatic.com/media/815797_2b4042d2f477479493cc249e34069ff2~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Comedy Store', 'https://static.wixstatic.com/media/815797_ac4a3f91be154e7fae49e29432d504d8~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Royal Caribbean', 'https://static.wixstatic.com/media/815797_65958979faf34b5498667fe79ad14b0a~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                  ['Norwegian Cruise Line', 'https://static.wixstatic.com/media/815797_fa8f42d037e14f4eaeec75b320613657~mv2.jpg/v1/fit/w_220,h_180,q_90,enc_avif,quality_auto/logo.jpg'],
                ].map(([name, url]) => (
                  <div
                    key={name + k}
                    title={name}
                    className="flex items-center justify-center h-24 w-40 shrink-0 opacity-90 hover:opacity-100 transition-opacity"
                  >
                    <img src={url} alt={name} className="max-h-16 max-w-full object-contain" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE TEAM */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 items-start">
          <Reveal className="lg:col-span-6">
            <SectionEyebrow>Meet the founder</SectionEyebrow>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
              People, first.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={150}>
            <p className="text-base text-[#4a4a4a] leading-relaxed">
              David Ball Management is led by founder David Ball, drawing on more than fifteen
              years across agenting, casting and production, and a considered commitment
              to the human relationship at the heart of every career the agency helps shape.
            </p>
          </Reveal>
        </div>

        <Reveal>
          <Link to="/about" className="block group">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-5 img-hover rounded-2xl overflow-hidden aspect-[1/1] bg-neutral-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_casting-collective-1/artifacts/gbgwl0sk_dsc07395_.webp"
                  alt="David Ball"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <p className="eyebrow">Agency Director</p>
                <h3 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase text-[#002b5c] mt-6 leading-[0.9] group-hover:text-[#C5A059] transition-colors">
                  David Ball
                </h3>
                <p className="mt-10 text-[#4a4a4a] leading-relaxed max-w-lg">
                  Over fifteen years as an agent, following work as an actor and
                  later a producer. Recipient of the Stage One / SOLT Apprentice
                  Bursary and, later, the Stage One / SOLT Bursary for New Producers.
                </p>
                <p className="mt-12 font-heading text-2xl md:text-3xl italic text-[#002b5c]/80 leading-tight border-l-2 border-[#C5A059] pl-6 max-w-lg">
                  "The agent&#39;s work is invisible when it is done well. That is the point."
                </p>
                <div className="mt-10 flex items-center gap-4">
                  <span className="w-8 h-px bg-[#C5A059]" />
                  <span className="text-[11px] tracking-[0.24em] uppercase text-[#6b6b6b]">
                    Founding Agent · London · Est. 2016
                  </span>
                </div>
                <p className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-[#002b5c] gold-underline">
                  Read the full story <ArrowUpRight size={14} className="text-[#C5A059]" />
                </p>
              </div>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* FEATURED ACTORS */}
      <section className="bg-[#002b5c] text-white overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
            <Reveal className="lg:col-span-6">
              <div className="flex items-center gap-4">
                <span className="w-10 h-px bg-[#C5A059]" />
                <span className="eyebrow text-[#C5A059]">Featured Clients</span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95]">
                Actors we have<br />the privilege of<br /><span className="italic font-light">representing.</span>
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:col-start-8" delay={150}>
              <p className="text-white/70 text-base leading-relaxed">
                A considered client list, working across British and international
                television, film, theatre, commercials and voice.
              </p>
              <Link to="/clients" className="mt-8 inline-flex items-center gap-3 text-[#C5A059] text-[11px] tracking-[0.24em] uppercase gold-underline">
                View our clients <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
            {featured.map((c, i) => (
              <Reveal key={c.id} delay={i * 100}>
                <Link to={`/clients/${c.slug}`} data-testid={`featured-client-${c.slug}`} className="block group">
                  <div className="img-hover rounded-xl overflow-hidden aspect-[3/4] bg-white/5">
                    <img src={c.headshot} alt={c.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <h3 className="font-heading text-xl md:text-2xl uppercase">{c.name}</h3>
                    <ArrowUpRight size={16} className="text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
          <Reveal className="lg:col-span-6">
            <SectionEyebrow>Latest News</SectionEyebrow>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
              From the desk.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:col-start-8 flex lg:justify-end" delay={150}>
            <Link to="/news" className="inline-flex items-center gap-3 text-[#002b5c] text-[11px] tracking-[0.24em] uppercase gold-underline">
              All news <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {latestNews.map((n, i) => (
            <Reveal key={n.id} delay={i * 120}>
              <Link to={`/news/${n.slug}`} className="block group">
                <div className="img-hover rounded-2xl overflow-hidden aspect-[3/4] bg-[#faf8f4]">
                  <img src={n.cover} alt={n.title} className="w-full h-full object-cover" />
                </div>
                <p className="eyebrow mt-6">{n.category} · {new Date(n.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                <h3 className="font-heading text-2xl md:text-3xl uppercase text-[#002b5c] mt-3 leading-tight group-hover:text-[#C5A059] transition-colors">{n.title}</h3>
                <p className="mt-3 text-sm text-[#4a4a4a] leading-relaxed">{n.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED GREEN ROOM */}
      {featuredArticle && (
        <section className="bg-[#faf8f4]">
          <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <Reveal className="lg:col-span-7">
                <Link to={`/green-room/${featuredArticle.slug}`} className="block group">
                  <div className="relative bg-white border border-black/8 rounded-2xl p-10 lg:p-14 hover:border-[#C5A059]/60 hover:shadow-[0_20px_60px_-20px_rgba(0,43,92,0.18)] transition-all duration-500">
                    <div className="flex items-start justify-between">
                      <span className="eyebrow text-[#C5A059]">The Green Room · N°01</span>
                      <span className="font-heading text-[#C5A059] text-sm tracking-wider uppercase">Featured</span>
                    </div>
                    <p className="eyebrow text-[#6b6b6b] mt-10">{featuredArticle.category}</p>
                    <h3 className="font-heading text-3xl md:text-4xl lg:text-[44px] uppercase text-[#002b5c] mt-4 leading-[1.02] group-hover:text-[#C5A059] transition-colors">
                      {featuredArticle.title}
                    </h3>
                    <div className="hairline mt-12 opacity-60" />
                    <div className="mt-6 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                      <span>{featuredArticle.author}</span>
                      <span>{featuredArticle.read_time}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
              <Reveal className="lg:col-span-5" delay={150}>
                <SectionEyebrow>The Green Room · Latest</SectionEyebrow>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
                  {featuredArticle.title}
                </h2>
                <p className="mt-6 text-base text-[#4a4a4a] leading-relaxed">
                  {featuredArticle.excerpt}
                </p>
                <div className="mt-8 flex items-center gap-6 text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                  <span>{featuredArticle.author}</span>
                  <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
                  <span>{featuredArticle.read_time}</span>
                </div>
                <Link to={`/green-room/${featuredArticle.slug}`} className="mt-10 inline-flex items-center gap-3 text-[#002b5c] text-[11px] tracking-[0.24em] uppercase gold-underline">
                  Read article <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {quotes.length > 0 && (
      <section data-testid="testimonials-section" className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <Reveal>
          <SectionEyebrow>What our clients say</SectionEyebrow>
        </Reveal>
        <div className={`grid gap-8 lg:gap-12 mt-14 ${quotes.length === 1 ? 'max-w-3xl' : quotes.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
          {quotes.map((t, i) => (
            <Reveal key={t.id} delay={i * 100}>
              <div className="card-lift bg-white border border-black/5 rounded-2xl p-10 h-full">
                <Quote size={28} className="text-[#C5A059]" />
                <p className="mt-6 font-heading text-2xl md:text-3xl leading-tight text-[#002b5c]">
                  "{t.quote}"
                </p>
                <div className="hairline mt-8 opacity-60" />
                <div className="mt-6">
                  <p className="font-heading text-lg uppercase text-[#002b5c]">{t.name}</p>
                  <p className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b] mt-1">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="relative bg-[#001a38] text-white overflow-hidden grain">
        <div className="absolute inset-0 opacity-40">
          <img src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=2000&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#001a38]/85" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 py-32 lg:py-48 text-center">
          <Reveal>
            <p className="eyebrow text-[#C5A059]">Representation Enquiry</p>
            <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl uppercase mt-8 leading-[0.9]">
              Could we be<br />the <span className="italic font-light">right</span> agency<br />for you?
            </h2>
            <p className="mt-8 max-w-xl mx-auto text-white/75 text-base md:text-lg leading-relaxed">
              We accept a small number of new actors each year. If your work resonates, we would love to hear from you.
            </p>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="home-final-cta">
                <span className="inline-flex items-center gap-3 rounded-full bg-[#C5A059] text-[#001a38] px-9 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-white transition-all duration-500 hover:-translate-y-0.5">
                  Apply for Representation <ArrowRight size={14} />
                </span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
